function toIcsDate(iso: string): string {
  return new Date(iso).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
}

export function buildGoogleCalendarUrl(params: {
  serviceName: string;
  slotStart: string;
  slotEnd: string;
}): string {
  const url = new URL('https://calendar.google.com/calendar/render');
  url.searchParams.set('action', 'TEMPLATE');
  url.searchParams.set('text', params.serviceName);
  url.searchParams.set('dates', `${toIcsDate(params.slotStart)}/${toIcsDate(params.slotEnd)}`);
  return url.toString();
}

export function buildSingleEventIcs(params: {
  uid: string;
  serviceName: string;
  slotStart: string;
  slotEnd: string;
}): string {
  const now = toIcsDate(new Date().toISOString());
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//BookingPlatform//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${params.uid}@booking-platform`,
    `DTSTAMP:${now}`,
    `DTSTART:${toIcsDate(params.slotStart)}`,
    `DTEND:${toIcsDate(params.slotEnd)}`,
    `SUMMARY:${params.serviceName.replace(/[\\;,]/g, (c) => `\\${c}`)}`,
    'STATUS:CONFIRMED',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n') + '\r\n';
}

function isTelegramWebApp(): boolean {
  return !!(window as any).Telegram?.WebApp?.openLink;
}

/**
 * Download / open an ICS file so the OS can add it to a calendar app.
 * - Telegram Mini App: opens the HTTPS URL in the native browser.
 * - iOS: Web Share API with a text/calendar File → iOS share sheet.
 *   Calendar.app accepts text/calendar files from the share sheet.
 *   webcal:// and https:// navigation from WhatsApp WKWebView are blocked/
 *   converted — Web Share API is the only reliable path from WhatsApp on iOS.
 * - Desktop/Android: standard hidden-anchor download.
 */
export function downloadIcs(icsContent: string, filename: string, httpsUrl?: string): void {
  if (isTelegramWebApp() && httpsUrl) {
    (window as any).Telegram.WebApp.openLink(httpsUrl);
    return;
  }

  const isIos = /iPhone|iPad|iPod/i.test(navigator.userAgent);

  // text/calendar is the correct MIME type for Calendar.app on iOS
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });

  if (isIos) {
    const file = new File([blob], filename, { type: 'text/calendar' });
    if (navigator.canShare?.({ files: [file] })) {
      navigator.share({ files: [file], title: filename }).catch(() => {});
      return;
    }
    // Very old iOS without Web Share API
    const url = URL.createObjectURL(blob);
    window.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    return;
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Build the HTTPS URL for the public ICS endpoint in booking-service.
 * Returns undefined when VITE_BOOKING_SERVICE_URL is not configured.
 */
export function buildBookingIcsUrl(bookingId: string, tenantId: string): string | undefined {
  const RAW = (import.meta.env.VITE_BOOKING_SERVICE_URL as string | undefined)?.replace(/\/$/, '');
  if (!RAW) return undefined;
  const isLocal = /localhost|127\.0\.0\.1|\[::1\]/.test(RAW);
  const base = import.meta.env.DEV && isLocal ? '/__booking_dev__' : RAW;
  return `${base}/api/v1/public/ics/booking/${encodeURIComponent(bookingId)}?tenant_id=${encodeURIComponent(tenantId)}`;
}
